import React, { useState, useContext, useEffect } from 'react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import LTVAttractiveness from './LTVAttractiveness/LTVAttractiveness';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import LTVMarketSignals from './LTVMarketSignals/LTVMarketSignals';
import RiskOverviewStandardSectionHeader from '/src/Components/RiskOverviewStandardSectionHeader/RiskOverviewStandardSectionHeader';
import HowWorriedShouldIBe from '/src/Components/HowWorriedShouldIBe/HowWorriedShouldIBe';
import { Repeat } from 'lucide-react';
import { WindowSizeContext } from '/src/App';

import './style.css';

const LTV = ({ propertyData, title }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Pull the housing market data from the context
    const { housingMarketData, userPersonalInfo } = useContext(AreaDataContext);

    // Pull the key information from the housingMarketData
    const originalValuesFromHousingMarketData = housingMarketData?.originalValues || null;
    const marketDataKeys = originalValuesFromHousingMarketData?.marketDataKeys || [];

    // Create the states
    const [currentStage, setCurrentStage] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Use useEffect to check if the data is loaded
    useEffect(() => {
        if (housingMarketData) {
            if (!dataLoaded) {
                setDataLoaded(true);
            }
        }
    }, [housingMarketData]);

    const newHousingMarketScoringData = housingMarketData?.refinedMarketData?.newMarketScores
    const newHousingMarketScoringFinalScore = newHousingMarketScoringData?.finalScore || 0;

    const newHousingMarketScoreText = newHousingMarketScoringData?.finalScoreTextRating || "Medium";

    let newHousingMarketScoreCSSClassRating = "low";
    let newHousingMarketScoreCSSClassPositiveNegative = "negative";
    let newHousingMarketScoreDislayedText = "Small";
    let newHousingMarketScoreDisplayTextPositiveNegative = "Negative";

    if (newHousingMarketScoringFinalScore > 0) {
        newHousingMarketScoreDisplayTextPositiveNegative = "positive";
    }

    if (newHousingMarketScoreText === 'Medium') {
        newHousingMarketScoreDislayedText = "Moderate";
        newHousingMarketScoreCSSClassRating = "medium";
    } else if (newHousingMarketScoreText === 'High') {
        newHousingMarketScoreDislayedText = "Large";
        newHousingMarketScoreCSSClassRating = "high";
    } else if (newHousingMarketScoreText === 'Low') {
        newHousingMarketScoreDislayedText = "Small";
        newHousingMarketScoreCSSClassRating = "medium";
    }

    const finalCSSClass = `${newHousingMarketScoreCSSClassRating} ${newHousingMarketScoreCSSClassPositiveNegative}`;

    const newHousingMarketScoreDisplayText = `${newHousingMarketScoreDislayedText} ${newHousingMarketScoreDisplayTextPositiveNegative} Signals`;


    const housingMarketIndividualDatapointScores = housingMarketData?.refinedMarketData?.marketScores?.individualDatapointScores || [];

    // Get the count of the number of datapoints that have either "change_magnitude_extreme" or "change_magnitude_significant" in the tags array
    const change_magnitude_extreme_count = housingMarketIndividualDatapointScores.filter(datapoint => datapoint.tags.includes('change_magnitude_extreme') || datapoint.tags.includes('change_magnitude_significant')).length;
    // console.log('change_magnitude_extreme_count:', change_magnitude_extreme_count);


    const attractiveness_risk_level_text = housingMarketData?.ltvScoring?.attractivenessRiskLevelText?.toLowerCase() || "low";
    const attractiveness_risk_level_css_class = attractiveness_risk_level_text;
    const attractiveness_risk_level_score = housingMarketData?.ltvScoring?.attractivenessScore || 0;
    const market_signals_risk_level_text = housingMarketData?.ltvScoring?.marketSignalsRiskLevelText?.toLowerCase() || "low";
    const market_signals_risk_level_score = housingMarketData?.ltvScoring?.marketSignalsScore || 0;

    // Get the average of the two risk levels rounded up to the nearest integer
    const risk_level_score = Math.round((attractiveness_risk_level_score + market_signals_risk_level_score) / 2);
    const dummy_risk_level_score_text = "Dummy text for the risk level score ";


    const userRelationToProperty = userPersonalInfo?.relationToProperty || "owner";

    let personalized_opening_sentence = "";
    let attractiveness_dynamic_sentence = "";
    let market_signals_dynamic_sentence = '';

    if (userRelationToProperty === "owner") {
        personalized_opening_sentence = "Since you own the home, maintaining value, if not increasing it, is a priority regardless of your timeline. ";
    } else if (userRelationToProperty === "buyer") {
        personalized_opening_sentence = "If you're considering buying this property, you likely care about resale value, even if you have no plans to sell anytime soon. ";
    }

    if (attractiveness_risk_level_text === 'high') {
        attractiveness_dynamic_sentence = "The projected future cost of ownership for this home is diverging from what households in this area can afford - you will be making the bet that the number of affluent buyers in the community would grow. ";
    } else if (attractiveness_risk_level_text === 'medium') {
        attractiveness_dynamic_sentence = "While the projected cost of ownership for this home is outpacing what the local community can afford, the gap may be manageable. Regardless, there still is a gap, so you'd want to see area household incomes increase faster than recent trends or the number of affluent households grow. ";
    } else {
        attractiveness_dynamic_sentence = "The good news is that the projected cost of ownership for this home is fairly in line with what households in your area can afford. ";
    };

    if (market_signals_risk_level_text === 'high') {
        market_signals_dynamic_sentence = "We're seeing a number of signals in the local market suggesting that climate risk is having an impact. ";
        if (change_magnitude_extreme_count > 2) {
            market_signals_dynamic_sentence += "In fact, when mapped alongside climate events, some of the trend shifts are quite significant and in the wrong direction. ";
        }
    } else if (market_signals_risk_level_text === 'medium') {
        market_signals_dynamic_sentence = "We're seeing early signals in the local market suggesting that climate risk is having an impact. ";
        if (change_magnitude_extreme_count > 2) {
            market_signals_dynamic_sentence += "When mapped alongside climate events, some clear negative trend shifts are already present. ";
        }
    } else {
        market_signals_dynamic_sentence = "Right now we're not seeing clear signs of climate risk impacting the local market. That doesn't mean the market itself is safe, just that climate doesn't seem to be having much of an impact as of today. ";
    }

    let attractiveness_market_combination_sentence = "";
    if (attractiveness_risk_level_text === 'high' && market_signals_risk_level_text === 'high') {
        attractiveness_market_combination_sentence = market_signals_dynamic_sentence + attractiveness_dynamic_sentence;
    } else if (attractiveness_risk_level_text === 'high' && market_signals_risk_level_text === 'medium') {
        attractiveness_market_combination_sentence = market_signals_dynamic_sentence + attractiveness_dynamic_sentence;
    } else if (attractiveness_risk_level_text === 'medium' && market_signals_risk_level_text === 'high') {
        attractiveness_market_combination_sentence = market_signals_dynamic_sentence + attractiveness_dynamic_sentence;
    } else if (attractiveness_risk_level_text === 'medium' && market_signals_risk_level_text === 'medium') {
        attractiveness_market_combination_sentence = attractiveness_dynamic_sentence + market_signals_dynamic_sentence;
    } else {
        attractiveness_market_combination_sentence = attractiveness_dynamic_sentence + market_signals_dynamic_sentence;
    }

    const combined_risk_level_text = personalized_opening_sentence + attractiveness_market_combination_sentence;

    const attractivenessRiskLevelText = `${attractiveness_risk_level_text.charAt(0).toUpperCase() + attractiveness_risk_level_text.slice(1)} Chance`;

    const ltv_types = [
        {
            type: 'card',
            id: 1,
            icon: "expenses-ltv-green.png",
            tagline: "Market Appeal",
            title: "Will this home's future ownership costs keep away potential buyers?",
            description: "As the cost of ownership goes up, fewer buys can afford those costs and a mortgage.",
            riskLevel: attractiveness_risk_level_text,
            riskLevelText: attractivenessRiskLevelText,
            riskLevelCSSClass: attractiveness_risk_level_css_class
        },
        {
            type: 'separator'
        },
        {
            type: 'card',
            id: 2,
            icon: "home-ltv-icon.png",
            tagline: "Market Forces on the Home",
            title: "Do we see any signs of climate risk impacting the local market today?",
            description: "A major part of your home's value is the location. If the local market is declining, your home's value will decline.",
            riskLevel: market_signals_risk_level_text,
            riskLevelText: newHousingMarketScoreDisplayText,
            riskLevelCSSClass: finalCSSClass
        }
    ]

    const handleStageChange = (stage) => {
        // Set current stage to the stage that was clicked, if the stage was already set to that, then set it to null
        setCurrentStage(currentStage === stage ? null : stage);
    }

    const ltv_intro_sentence = "Climate risk can dramatically impact home values over time, but catching those signals can be challenging. We look for warning signs examining the home's future from different directions - its attractiveness to future buyers and market forces it’ll be subject to:"

    const clickToLearnMoreSentence = "Click on a category to learn more"

    return (
        <div className="tab-content-container">
            <RiskOverviewStandardSectionHeader title={title} />
            {!currentStage &&
                <p className="text-align-center" dangerouslySetInnerHTML={{ __html: ltv_intro_sentence }} />
            }
            <div className={`ltv-types-container ${deviceType}`}>
                {ltv_types.map((item, index) => {
                    // console.log('currentStage:', currentStage);
                    return (
                        <div key={`ltv-item-${index}`}>
                            {item.type === 'card' ? (
                                <div
                                    className={`ltv-type ${(currentStage) ? (currentStage === item.id) ? 'active' : 'inactive' : null} ${item.riskLevelCSSClass}`}
                                    onClick={() => dataLoaded && handleStageChange(item.id)}
                                >
                                    <div className="ltv-type-tag">
                                        {item.tagline}
                                    </div>
                                    <div className="ltv-type-info-container">
                                        <div className="ltv-type-image">
                                            <img src={CreateCloudfrontUrl(item.icon)} alt={item.title} />
                                        </div>
                                        <div className="ltv-type-text">
                                            <h4 className='text-align-center'>{item.title}</h4>
                                        </div>
                                    </div>
                                        <button className={`ltv-type-button ${item.riskLevelCSSClass}`}>
                                            {/* {item.riskLevel.toLowerCase() === 'high' ? 'High' : item.riskLevel.toLowerCase() === 'medium' ? 'Medium' : 'Low'} Likelihood */}
                                            {item.riskLevelText}
                                        </button>
                                </div>
                            ) : (
                                <div className="ltv-types-seperator-icon-container">
                                    <Repeat />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            {(currentStage && housingMarketData) &&
                <div className={`ltv-type-deepdive-container ${deviceType}`}>

                    <CurlyBraceDivider />
                    {currentStage === 1 &&
                        <LTVAttractiveness
                            originalValues={originalValuesFromHousingMarketData}
                            ltvInfo={ltv_types[0]}

                        />
                    }
                    {currentStage === 2 &&
                        <LTVMarketSignals
                            housingMarketData={housingMarketData}
                            propertyData={propertyData}
                            marketDataKeys={marketDataKeys}
                            ltvInfo={ltv_types[2]}
                        />
                    }
                    {/* Add a close button for mobile */}
                    {(deviceType === 'mobile' && currentStage !== null) &&
                        <button className="ltv-type-deepdive-close-button" onClick={() => setCurrentStage(null)}>
                            Close
                        </button>
                    }
                </div>
            }
            {/* {!currentStage &&
                <p className="text-align-center ltv-click-to-learn-more-sentence" dangerouslySetInnerHTML={{ __html: clickToLearnMoreSentence }} />
            } */}
            <HowWorriedShouldIBe
                meterLevel={risk_level_score}
                sentence={combined_risk_level_text}
                userPersonalInfo={userPersonalInfo}
                type="long-term-value"
            />
        </div>
    )
}

export default LTV;